'use-client';
import React, { useCallback, useEffect, useMemo } from 'react';
import classNames from 'clsx';
import { formatClassNames, debounce } from '@wix/editor-elements-common-utils';
import type { IMenuContentProps } from '../../Menu.types';
import { MenuItem } from '../../viewer/components/MenuItem/MenuItem';
import classes from './style/MenuContent.scss';
import { getCurrentMenuItem } from '../../../../common/menu/getCurrentMenuItem';
import { ScrollControls } from '../../viewer/components/ScrollControls/ScrollControls';
import {
  scrollToMenuItem,
  rootLevelMenuItemSelector,
  MINIMAL_IMPORTANT_SCROLL_DISTANCE,
  scrollMenu,
} from '../../../../common/menu/Scroll/utils';
import { useResizeObserver } from '@wix/thunderbolt-elements/providers/useResizeObserver';
import { semanticClassNames as menuSemanticClassNames } from '../../Menu.semanticClassNames';
import shmSemanticClassNames from '../../../StylableHorizontalMenu/StylableHorizontalMenu.semanticClassNames';
import { useMenuContext } from '../../viewer/MenuContext';
import { dataSelectors } from '../../viewer/components/MenuItem/showDropdown';
import { DataHooks } from '../../constants';
import { calcLastItemWithLinkOrSubItems } from './utils';

const DEBOUNCE_DURATION = 100;

const MenuContent: React.FC<IMenuContentProps> = props => {
  const {
    id,
    className,
    onItemMouseIn,
    onItemMouseOut,
    onItemClick,
    onItemDblClick,
  } = props;

  const {
    items,
    partToPreviewStateMap,
    currentUrl,
    activeAnchor,
    translations,
    menuStyleId,
    customClassNames = [],
    getAnimationName,
  } = useMenuContext();

  const rootClassName = classNames(
    classes.root,
    formatClassNames(shmSemanticClassNames.root),
    formatClassNames(menuSemanticClassNames.root, ...customClassNames),
  );

  const menuContainerRef = React.useRef<HTMLElement>(null); // for DOM measurements
  const [isScrollable, setIsScrollable] = React.useState(false); // clientWidth < scrollWidth
  const [isScrollBackwardButtonVisible, setIsScrollBackwardButtonVisible] =
    React.useState(false); // has hidden menu item(s) in the left side
  const [isScrollForwardButtonVisible, setIsScrollForwardButtonVisible] =
    React.useState(false); // has hidden menu item(s) in the right side

  const lastItemWithLinkOrSubItems = useMemo(
    () => calcLastItemWithLinkOrSubItems(items),
    [items],
  );
  const currentItem = useMemo(
    () => getCurrentMenuItem(items, activeAnchor, currentUrl),
    [items, activeAnchor, currentUrl],
  );

  const handleScrollBackward = useCallback((): void => {
    if (menuContainerRef.current && isScrollable) {
      scrollMenu(menuContainerRef.current, 'backward');
    }
  }, [isScrollable]);

  const handleScrollForward = useCallback((): void => {
    if (menuContainerRef.current && isScrollable) {
      scrollMenu(menuContainerRef.current, 'forward');
    }
  }, [isScrollable]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateScrollState = useCallback(
    debounce(() => {
      const { current: nav } = menuContainerRef;
      if (!nav) {
        return;
      }
      const { scrollWidth, clientWidth } = nav;
      const scrollLeft = Math.abs(nav.scrollLeft);
      const isMenuScrollable = clientWidth < scrollWidth;
      setIsScrollable(isMenuScrollable);
      setIsScrollBackwardButtonVisible(
        isMenuScrollable && scrollLeft > MINIMAL_IMPORTANT_SCROLL_DISTANCE,
      );
      setIsScrollForwardButtonVisible(
        isMenuScrollable &&
          scrollLeft <
            scrollWidth - clientWidth - MINIMAL_IMPORTANT_SCROLL_DISTANCE,
      );
    }, DEBOUNCE_DURATION),
    [],
  );

  const updateScrolledLeftCSSVar = useCallback(() => {
    const { current: nav } = menuContainerRef;
    if (!nav) {
      return;
    }

    nav.style.setProperty('--scrolled-left', `-${nav.scrollLeft}px`);
  }, []);

  const handleOnScroll = useCallback(() => {
    updateScrollState();
    updateScrolledLeftCSSVar();
  }, [updateScrollState, updateScrolledLeftCSSVar]);

  const focusOnMenuItem = useCallback(
    (menuItem: HTMLElement) => {
      const { current: menuContainer } = menuContainerRef;

      if (!isScrollable || !menuItem || !menuContainer) {
        return;
      }
      const rootMenuItem = menuContainer.closest<HTMLElement>(
        rootLevelMenuItemSelector,
      );
      if (rootMenuItem && rootMenuItem !== menuItem) {
        scrollToMenuItem(menuContainer, rootMenuItem);
      } else {
        scrollToMenuItem(menuContainer, menuItem);
      }
    },
    [isScrollable],
  );

  const handleOnFocus = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      const targetMenuItem = event.target;
      focusOnMenuItem(targetMenuItem);
    },
    [focusOnMenuItem],
  );

  useResizeObserver({
    ref: menuContainerRef,
    callback: updateScrollState,
  });
  useEffect(updateScrollState, [items, updateScrollState]);

  return (
    <div id={id} className={className}>
      <nav
        id={menuStyleId}
        className={rootClassName}
        ref={menuContainerRef}
        {...(isScrollable && {
          onScroll: handleOnScroll,
          onFocus: handleOnFocus,
        })}
        {...{ [dataSelectors.nav]: true }}
        data-hook={DataHooks.menuRoot}
        aria-label={translations.menuNavAriaLabel}
      >
        <ul className={classes.container}>
          {items.map(item => (
            <MenuItem
              hideDivider={item.id === lastItemWithLinkOrSubItems}
              translations={translations}
              currentItem={currentItem}
              item={item}
              key={item.id}
              partToPreviewStateMap={partToPreviewStateMap}
              onItemClick={onItemClick}
              focusOnMenuItem={focusOnMenuItem}
              onItemMouseIn={onItemMouseIn}
              onItemMouseOut={onItemMouseOut}
              onItemDblClick={onItemDblClick}
              getAnimationName={getAnimationName}
            />
          ))}
        </ul>
        <ScrollControls
          scrollPageForward={handleScrollForward}
          scrollPageToBackward={handleScrollBackward}
          isScrollBackwardButtonShown={isScrollBackwardButtonVisible}
          isScrollForwardButtonShown={isScrollForwardButtonVisible}
          previewState={partToPreviewStateMap?.['scroll-button']}
        />
      </nav>
    </div>
  );
};

export default MenuContent;
